/**
 * Codey Thinking Component
 * Displays AI thought process like ChatGPT's "Thought for X seconds"
 */

import React, { useState, useEffect } from 'react';

interface CodeyThinkingProps {
  isThinking: boolean;
  onThinkingComplete?: () => void;
}

export const CodeyThinking: React.FC<CodeyThinkingProps> = ({ isThinking, onThinkingComplete }) => {
  const [elapsed, setElapsed] = useState(0);
  const [currentThought, setCurrentThought] = useState('');

  const thoughts = [
    "Analyzing your request...",
    "Planning the project structure...",
    "Considering best practices...",
    "Designing the architecture...",
    "Choosing optimal tech stack...",
    "Thinking about user experience...",
    "Structuring the components...",
    "Planning the file organization..."
  ];

  useEffect(() => {
    if (!isThinking) {
      setElapsed(0);
      setCurrentThought('');
      return;
    }

    // Update elapsed time
    const timer = setInterval(() => {
      setElapsed(prev => prev + 1);
    }, 1000);

    // Cycle through thoughts
    let thoughtIndex = 0;
    const thoughtTimer = setInterval(() => {
      setCurrentThought(thoughts[thoughtIndex % thoughts.length]);
      thoughtIndex++;
    }, 2000);

    return () => {
      clearInterval(timer);
      clearInterval(thoughtTimer);
      if (onThinkingComplete) {
        onThinkingComplete();
      }
    };
  }, [isThinking]);

  if (!isThinking) return null;

  return (
    <div className="flex gap-2 animate-fade-in">
      <div className="w-6 h-6 rounded-lg flex items-center justify-center bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-600 shadow-sm flex-shrink-0">
        <span className="text-sm">🤔</span>
      </div>
      <div className="flex-1">
        <div className="backdrop-blur-sm px-3 py-2 rounded-lg shadow-sm bg-slate-800/70 border border-white/5">
          <div className="flex items-center gap-2 mb-1">
            <div className="flex gap-1">
              <div className="w-1.5 h-1.5 rounded-full animate-bounce bg-blue-400 shadow-sm" style={{ animationDelay: '0ms' }}></div>
              <div className="w-1.5 h-1.5 rounded-full animate-bounce bg-indigo-400 shadow-sm" style={{ animationDelay: '150ms' }}></div>
              <div className="w-1.5 h-1.5 rounded-full animate-bounce bg-blue-400 shadow-sm" style={{ animationDelay: '300ms' }}></div>
            </div>
            <span className="text-xs font-medium text-blue-300">
              Thought for {elapsed} second{elapsed !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="text-xs text-gray-300 italic">
            {currentThought}
          </div>
        </div>
      </div>
    </div>
  );
};

interface PlanningMessageProps {
  plan: {
    projectName: string;
    enhancedPrompt: string;
    features: string[];
    design: {
      style: string;
      colorScheme: string;
      layout: string;
    };
    techStack: string[];
  };
}

export const PlanningMessage: React.FC<PlanningMessageProps> = ({ plan }) => {
  return (
    <div className="flex gap-2 animate-fade-in">
      <div className="w-6 h-6 rounded-lg flex items-center justify-center bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-600 shadow-sm flex-shrink-0">
        <span className="text-sm">💡</span>
      </div>
      <div className="flex-1">
        <div className="backdrop-blur-sm px-4 py-3 rounded-lg shadow-sm bg-slate-800/70 border border-white/5">
          <div className="text-sm font-semibold text-blue-300 mb-2">
            ✨ Here's what I'm going to build for you:
          </div>
          
          <div className="space-y-3 text-xs text-gray-300">
            <div>
              <span className="font-semibold text-white">Project:</span> {plan.projectName}
            </div>
            
            {plan.design && (
              <div>
                <span className="font-semibold text-white">Design Inspiration:</span>
                <div className="mt-1 text-gray-400">
                  {plan.design.style} with {plan.design.colorScheme} colors and {plan.design.layout} layout
                </div>
              </div>
            )}
            
            {plan.features && plan.features.length > 0 && (
              <div>
                <span className="font-semibold text-white">Features:</span>
                <ul className="mt-1 space-y-1 list-disc list-inside text-gray-400">
                  {plan.features.slice(0, 5).map((feature, i) => (
                    <li key={i}>{feature}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {plan.techStack && plan.techStack.length > 0 && (
              <div>
                <span className="font-semibold text-white">Tech Stack:</span>
                <div className="mt-1 flex flex-wrap gap-1">
                  {plan.techStack.map((tech, i) => (
                    <span key={i} className="px-2 py-0.5 bg-blue-500/20 text-blue-300 rounded text-[10px] font-medium">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <div className="mt-3 text-xs text-blue-300 font-medium">
            🚀 Let me build this for you...
          </div>
        </div>
      </div>
    </div>
  );
};

export const ProgressMessage: React.FC<{ message: string; emoji?: string }> = ({ message, emoji = '⚡' }) => {
  return (
    <div className="flex gap-2 animate-fade-in">
      <div className="w-6 h-6 rounded-lg flex items-center justify-center bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-600 shadow-sm flex-shrink-0">
        <span className="text-sm">{emoji}</span>
      </div>
      <div className="flex-1">
        <div className="backdrop-blur-sm px-3 py-2 rounded-lg shadow-sm bg-slate-800/70 border border-white/5">
          <div className="text-xs text-gray-300">
            {message}
          </div>
        </div>
      </div>
    </div>
  );
};
